import { ArrowTopRightIcon, CaretLeftIcon } from '@radix-ui/react-icons';
import { Avatar, Button, Skeleton } from '@radix-ui/themes';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { useAppNavigation } from 'helpers/useAppNavigation';
import { useWindowSize } from 'helpers/useWindowSize';
import { openInExternalUrl } from 'helpers/utils';
import type { CompetitionType } from 'types/competition';
import FollowUnfollowButton from './FollowUnfollowButton';
import PlayerObjectSkeleton from './PlayerObjectSkeleton';

export const NoMenuHeader: React.FC<
  Partial<CompetitionType> & {
    loading: boolean;
    defaultName: string;
    fitpLink?: string;
  }
> = ({ id, name, followed, followable_type, loading, defaultName, fitpLink }) => {
  const { navigateBack } = useAppNavigation();
  const size = useWindowSize();
  const { ref, inView } = useInView({
    threshold: size.width > 768 ? 1 : 0.4,
  });
  const subMenuSticky = size.height > 600;

  const buttons = (
    <div id='buttons' className='flex items-center justify-center gap-2 self-stretch p-2 md:items-end md:justify-end'>
      {followable_type && (
        <div id='segui'>
          <FollowUnfollowButton id={id!} type={followable_type!} followed={followed!} />
        </div>
      )}
      {fitpLink && (
        <div id='external-link'>
          <Button
            variant='outline'
            size='2'
            className='min-w-28 cursor-pointer'
            onClick={() => openInExternalUrl(fitpLink)}
          >
            Link FITP
            <ArrowTopRightIcon />
          </Button>
        </div>
      )}
    </div>
  );

  const miniHeader = (
    <div
      className={classNames(
        { 'sticky z-9 md:top-[calc(8rem-1px)]': subMenuSticky },
        { 'top-20': subMenuSticky && !Capacitor.isNativePlatform() },
        { 'top-30': subMenuSticky && Capacitor.isNativePlatform() },
        'bg-neutral-neutral-1 flex flex-row items-center justify-center gap-2 rounded-xl p-1',
      )}
    >
      <div
        className='border-neutral-neutral-11 flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid'
        onClick={navigateBack}
      >
        <CaretLeftIcon className='text-neutral-neutral-11 h-6 w-6' />
      </div>
      <Avatar
        alt='Avatar Torneo'
        className='rounded-xl'
        src={'/images/tennistournament-avatar.jpg'}
        fallback={<img src='/images/tennistournament-avatar.jpg'></img>}
      />

      <div className='flex items-center gap-2 font-bold'>{name ? name : defaultName}</div>
    </div>
  );

  if (loading) {
    return (
      <>
        <div className='bg-neutral-neutral-1 mt-3 flex flex-col items-start gap-2 rounded-xl p-3 md:flex-row md:items-end'>
          <PlayerObjectSkeleton bigHeader={true} />
          <Skeleton className='h-8 w-16' />
        </div>
      </>
    );
  }

  return (
    <>
      <div className='bg-neutral-neutral-1 mt-3 flex flex-col items-start gap-2 rounded-xl p-3 md:flex-row' ref={ref}>
        {size.width < 1024 && (
          <div
            className='border-neutral-neutral-11 flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full border border-solid'
            onClick={navigateBack}
          >
            <CaretLeftIcon className='text-neutral-neutral-11 h-6 w-6' />
          </div>
        )}
        <div id='club' className='flex w-full items-start gap-2 self-stretch p-2'>
          <Avatar
            alt='Avatar Torneo'
            className='flex h-24 w-24 rounded-xl'
            src={'/images/tennistournament-avatar.jpg'}
            fallback={<img src='/images/tennistournament-avatar.jpg'></img>}
          />

          <div className='flex items-center gap-2 self-stretch p-2 text-2xl font-bold'>{name ? name : defaultName}</div>
        </div>
        {buttons}
      </div>
      {!inView && miniHeader}
    </>
  );
};
export default NoMenuHeader;
